import { map, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, zip } from 'rxjs';
import { LayerContainerService } from '../../shared/service/layer-container.service';
import { QueryService } from '../../shared/service/query.service';
import { ConfigService } from '../../config/config.service';
import { ArbeitsbereichService } from '../../shared/service/arbeitsbereich.service';
import { CONSTANTS_VALUE } from '../../config/constants.module';

import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';

import { IServerSideGetRowsRequest, ColumnState } from 'ag-grid-community';
import { LayerInfosService } from '../../shared/service/layer-infos.service';
import { AttributeType } from '../../shared/enum/attribute-type.enum';
import { ListViewColumn, ListViewTab } from '../../config/model/listview-config.model';
import { AgGridUtil } from '../utilities/ag-grid-util';
import { ColumnFilterUtil } from '../utilities/column-filter-util';
import { MainMapService } from '../../shared/service/main-map.service';
import { SelectionNotifyService } from '../../shared/service/selection-notify.service';
import { DetailViewFeatureParams } from '../../component/detail-view/detail-view.model';
import { JustMapExtentService } from './just-map-extent.service';
import {ErrorHandlingService} from '../../core/service/error-handling.service';
import {SelectionService} from "../../shared/service/selection.service";

@Injectable({
  providedIn: 'root',
})
export class AgGridListViewService {
  public tabsList: Array<any>;
  public totalTableRecords: number;

  private readonly activeTabBehaviorSubject: BehaviorSubject<string> = new BehaviorSubject(null);

  public get activeTabID(): string {
    return this.activeTabBehaviorSubject.value;
  }

  constructor(
    private layerContainerService: LayerContainerService,
    private configService: ConfigService,
    private queryService: QueryService,
    private arbeitsbereichService: ArbeitsbereichService,
    private translate: TranslateService,
    private layerInfosService: LayerInfosService,
    private mainMapService: MainMapService,
    private errorHandlingService: ErrorHandlingService,
    private selectionNotifyService: SelectionNotifyService,
    private justMapExtentService: JustMapExtentService,
    private selectionService: SelectionService
  ) {}

  /* SERVER-SIDE LOADING AG-GRID
  ==================================== */

  public loadFeaturesServerSide<T>(
    agGridRequest: IServerSideGetRowsRequest,
    layerUrl: string
  ): Observable<any[]> {
    const where = this.buildAgGridFilterModelWhere(agGridRequest.filterModel);
    const orderByFields = this.createOrderByFields(agGridRequest.sortModel);
    const mapExtent = this.getMapExtent();
    // build query
    const query = this.queryService.buildQuery(
      false,
      where,
      ['*'],
      orderByFields,
      mapExtent,
      agGridRequest.startRow,
      agGridRequest.endRow - agGridRequest.startRow
    );
    // execute query
    return this.queryService.execute(layerUrl, query, 'ag-grid-query execute').pipe(
      map(
        (featureSet: __esri.FeatureSet) => {
          return featureSet.features;
        },
        error => {
          console.error(error);
        }
      )
    );
  }

  /**
   * @function getAgGridFeaturesCount
   * @returns {Observable<number>}
   * @description Get total Count von alle Features, welche den Query matchen
   */
  public getAgGridFeaturesCount(
    agGridRequest: IServerSideGetRowsRequest,
    layerUrl: string
  ): Observable<number> {
    let where = this.buildAgGridFilterModelWhere(agGridRequest.filterModel);
    if (!where || where === '') where = '1=1';
    const mapExtent = this.getMapExtent();
    const query = this.queryService.buildQuery(false, where, null, null, mapExtent);
    const errorCode = CONSTANTS_VALUE.LAYER_ERROR.COUNT_FEATURES_FROM_LAYER_FAILED;
    return this.queryService.executeForCount(layerUrl, query, errorCode);
  }

  public loadAllFeatureIds(
    columnState: ColumnState[],
    layerUrl: string,
    totalRows: number
  ): Observable<number[]> {
    const where = '1=1';
    const orderByFields = [];

    return this.getFeatureIds(layerUrl, where, orderByFields, totalRows);
  }

  public selectFeaturesOnMap(selectedObjectIds: number[], layerUrl: string) {
    this.getSelectedFeaturesByIds(selectedObjectIds, layerUrl).subscribe(
      featureSet => {
        this.selectionService.removeAllSelectedGraphics();
        this.selectionService.addSelectedGraphic(featureSet.features[0]);
        this.selectionService.zoomOnFeature(featureSet.features[0]);

        this.layerInfosService.getLayerInfos(layerUrl).subscribe(layerInfo => {
          const detailViewFeatureParams: DetailViewFeatureParams = this.createFeatureParams(
            featureSet,
            layerInfo
          );
          this.selectionNotifyService.selectionFeatures(
            [featureSet.features[0].attributes.OBJECTID],
            detailViewFeatureParams
          );
        });
      },
      error => {
        this.errorHandlingService.handleLayerError(error);
      }
    );
  }

  public buildAgGridFilterModelWhere(filterModel: any): string {
    if (filterModel === null || filterModel === undefined) return '';

    const copy = AgGridUtil.setFilterFixGet(filterModel);
    return ColumnFilterUtil.createWhereSql(copy);
  }

  public createOrderByFields(sortModel: any[]): string[] {
    let appendObjectId = true;
    const sortParts = sortModel.map(part => {
      appendObjectId = appendObjectId && part.colId !== 'OBJECTID';
      return `${part.colId} ${part.sort}`;
    });

    // Allways inclyde OBJECTID in sort to ensure same tie breaker
    if (appendObjectId) {
      sortParts.push('OBJECTID asc');
    }

    /* added for performance reason:
    ORDER BY OBJECTID ASC, GLOBALID ASC is (for whatever reason) much faster than ORDER BY OBJECTID ASC
    although it has no effect on sort order
     */
    /* ToDo gewisse Umwelt Services haben kein GlobalID (z. B.)
    https://geo-dev.sbb.ch/site/rest/services/UMWELT_PUBLIC/NKBS_STANDORT_1_1_0/MapServer/4 */
    // sortParts.push('GLOBALID asc');

    return sortParts;
  }

  /**
   * @function getListViewTabConfig
   * @returns liste visible layers
   * @description Load aus den Configs definierten Layers für den gewählten Arbeitsbereich
   * und erstelle für jeden Layer ein neues Tab Element für die List-View
   */
  public getListViewTabConfig(): Observable<ListViewTab[]> {
    const currentArbeitsBereich = this.arbeitsbereichService.selectedArbeitsbereich;
    if (!currentArbeitsBereich) return of([]);
    const listTabs = this.configService.listViewConfig.listTabs.find(
      f => f.fkArbeitsbereich === currentArbeitsBereich.id
    );
    if (!listTabs) return of([]);
    const lyrContainer = this.layerContainerService.layerList.map(f => f.esriLayer);

    this.tabsList = [];
    const observables: Observable<ListViewTab>[] = [];

    listTabs.tabs.forEach((tab, tabIndex) => {
      const listViewLayers = lyrContainer.find(f => f.id === tab.fkLayer);
      if (listViewLayers) {
        const tabLayer: __esri.Layer = this.getSubLayerFromListViewLayers(
          listViewLayers,
          tab.layerId
        );
        observables.push(this.createTabProperties(tab, tabLayer, tabIndex));
      }
    });

    return forkJoin(...observables).pipe(
      tap(result => {
        this.tabsList = result;
      })
    );
  }

  /**
   * @param tabId the url of the service to get the data from
   * @param whereClause whereclause (default 1=1)
   * @param startNum first record number (for paging)
   * @param featureNum last record number (for paging)
   * @param orderByFields order By Fields
   * @param outFields the outfields (default Primary display field)
   * @param queryExtent
   */
  public getFeatures(
    tabId: string,
    whereClause?: string,
    startNum?: number,
    featureNum?: number,
    orderByFields?: string[],
    outFields?: string[],
    queryExtent?: __esri.Geometry
  ): Observable<any> {
    // const queryTask = this.queryService.buildQueryTask(tabId);
    // queryTask.requestOptions = { timeout: 900000 };
    if (!whereClause) whereClause = '1=1';
    const query = this.queryService.buildQuery(
      false,
      whereClause,
      null,
      orderByFields,
      queryExtent
    );
    if (startNum) query.start = startNum;
    if (featureNum) query.num = featureNum;
    query.outFields = this.getOutFieldsForQuery(tabId);
    const errorCode = CONSTANTS_VALUE.LAYER_ERROR.GET_FEATURES_FROM_LAYER_FAILED;
    return this.queryService.execute(tabId, query, errorCode);
  }

  /**
   * @name matchFeatureAttributeDomain
   * @param lyrFields
   * @param tabAttributes
   * @description map  domain codedvalue  value  on name
   */
  public matchFeatureAttributeDomain(lyrFields, tabAttributes): any {
    lyrFields.forEach(field => {
      if (tabAttributes.attributes[field.name] != null) {
        if (field.domain) {
          this.setDomainValue(field, tabAttributes);
        } else if (field.type === 'esriFieldTypeSmallInteger') {
          // 1 oder 0 wenn es sich um ein checkbox handelt
          if (tabAttributes.attributes[field.name] === 1) {
            this.translate.get('COMMON.YES').subscribe(label => {
              tabAttributes.attributes[field.name] = label;
            });
          } else if (tabAttributes.attributes[field.name] === 0) {
            this.translate.get('COMMON.NO').subscribe(label => {
              tabAttributes.attributes[field.alias] = label;
            });
          }
        } else if (field.type === 'esriFieldTypeDate') {
          // convert the date string from identify into real UTC date object and set date on ngModel
          const momentDate = moment.utc(
            new Date(tabAttributes.attributes[field.name]),
            'DD.MM.YYYY'
          );
          tabAttributes.attributes[field.alias] = momentDate.format('DD.MM.YYYY');
        }
      }
    });
    return tabAttributes;
  }

  public getDomainForSetFilter(outFields: string[], tabId: string): Observable<__esri.FeatureSet> {
    // execute query
    return this.queryService.executeForDomainValues(tabId, outFields, '1=1').pipe(
      map(
        (featureSet: __esri.FeatureSet) => {
          return featureSet;
        },
        error => {
          console.error(error);
        }
      )
    );
  }

  setActiveTabId(activeTabId: string) {
    this.activeTabBehaviorSubject.next(activeTabId);
  }

  /*-- private methods --*/

  private getSelectedFeaturesByIds(
    selectedObjectIds: number[],
    layerUrl: string
  ): Observable<__esri.FeatureSet> {
    const whereQuery = `OBJECTID in (${selectedObjectIds.join(',')})`;
    const query = this.queryService.buildQuery(true, whereQuery);
    const errorHandling = CONSTANTS_VALUE.LAYER_ERROR.GET_FEATURES_FROM_LAYER_WITH_GEOMETRY;
    return this.queryService.execute(layerUrl, query, errorHandling);
  }

  /**
   * returns a specific sublayer from a specified layer,
   * if it has no sublayers, the layer itself is returned
   * @param layerForListView the mapimageLayer to get the sublayer from
   * @param layerId the layerId
   */
  private getSubLayerFromListViewLayers(
    layerForListView: __esri.Layer,
    layerId: number
  ): __esri.Layer {
    let returnVal = layerForListView;
    const subLayers = layerForListView['allSublayers'].items;
    if (subLayers.length > 0) returnVal = subLayers.filter(f => f.id === layerId)[0];
    return returnVal;
  }

  /**
   * @param field
   * @param tabAttributes
   * @description resolve domain codedvalues and set attribute value
   */
  private setDomainValue(field, tabAttributes) {
    if (isNaN(parseInt(tabAttributes.attributes[field.name]))) return;
    const _domain = this.arbeitsbereichService.getDomain(
      field,
      tabAttributes.attributes[field.name]
    );
    tabAttributes.attributes[field.alias] = _domain[0] ? _domain[0].name : null;
  }

  /**
   * @param tabId
   * @return the specified fields for the query as array
   */
  private getOutFieldsForQuery(tabId: string): Array<string> {
    const tabConfig = this.tabsList.find(f => f.tabLayer.url === tabId);
    if (tabConfig) return tabConfig.columns.map(col => col.prop.replace('attributes.', ''));
    return ['*'];
  }

  private setDomainOfColumns(layerInfo: any, columns: ListViewColumn[]): ListViewColumn[] {
    columns.forEach(col => {
      if (col.type === AttributeType.domain || col.type === AttributeType.boolean) {
        const fieldName = col.prop.split('.');
        if (layerInfo.data.fields) {
          const field = layerInfo.data.fields.filter(f => f.name === fieldName[1])[0];
          if (field.domain) {
            col.domain = field.domain.codedValues;
          }
        }
      }
    });
    return columns;
  }

  private createTabProperties(
    tab: ListViewTab,
    tabLayer: __esri.Layer,
    tabIndex: number
  ): Observable<ListViewTab> {
    return this.layerInfosService.getLayerInfos(tabLayer['url']).pipe(
      map(layerInfo => {
        const listTab: ListViewTab = {
          fkLayer: tab.fkLayer,
          layerId: tab.layerId,
          title: tab.title,
          tabLayer: tabLayer,
          columns: this.setDomainOfColumns(layerInfo, tab.columns),
          orderByFields: tab.orderByFields ? tab.orderByFields : null,
          position: tabIndex,
          fitColumnsSize: tab.fitColumnsSize,
          hasGeometry: tab.hasGeometry !== undefined ? tab.hasGeometry : true,
        };
        return listTab;
      })
    );
  }

  private getFeatureIds(
    layerUrl: string,
    whereClause: string,
    orderByFields: string[],
    totalRows: number
  ) {
    const calls = this.featureCalls(
      CONSTANTS_VALUE.maxRecordCount,
      layerUrl,
      whereClause,
      orderByFields,
      totalRows
    );
    return zip(...calls).pipe(map((ids: number[]) => [].concat(...ids)));
  }

  private featureCalls(
    maxRecordCount: number,
    layerUrl: string,
    whereClause: string,
    orderByFields: string[],
    totalRows: number
  ) {
    const requestCount = Math.ceil(totalRows / maxRecordCount);
    let startNum = 0;
    const calls = [];
    for (let i = 0; i < requestCount; i++) {
      calls.push(
        this.queryService.executeForId(
          layerUrl,
          whereClause,
          orderByFields,
          startNum,
          maxRecordCount
        )
      );
      startNum += maxRecordCount;
    }
    return calls;
  }

  private createFeatureParams(featureSet: __esri.FeatureSet, layerInfo): DetailViewFeatureParams {
    const fkLayerId: string = this.arbeitsbereichService.selectedArbeitsbereich.editableLayers[0]
      .fkLayer;
    const esriLyr = this.layerContainerService.layerList.filter(
      f => f.esriLayer.id === fkLayerId && f.esriLayer.type === 'feature'
    )[0];

    if (!esriLyr) return;
    const editableFeatureLayer: __esri.FeatureLayer = esriLyr.esriLayer as __esri.FeatureLayer;

    return {
      currentArbeitsBereichLyrs: [{ esriLayer: editableFeatureLayer }],
      displayFieldname: '',
      feature: featureSet.features[0],
      layerId: editableFeatureLayer.layerId,
      createMode: false,
      workspace: this.arbeitsbereichService.selectedArbeitsbereich.id,
    };
  }

  public getMapExtent(): __esri.Geometry {
    if (this.justMapExtentService.justMapExtent) {
      return this.mainMapService.mapView.extent;
    } else {
      return null;
    }
  }
}
